// Scene 2 — heavenly sim scene. Character creation runs over it.
//
// ASSET SLOT — replace '' with import path once asset lands:
//   import heaven from "../assets/heaven.png";  → SCENE_BG

import { callIntake, type HistoryTurn } from "./lib/chat";

import _sceneBg from "../assets/simulation.png";

const SCENE_BG = _sceneBg;

// Authored lines — not LLM
const LINE_WAKE =
  "You're awake. This is a simulation. You can leave any time. You can change anything. Take your time.";
const LINE_NAME = "What should we call you?";
const LINE_BODY_OPENER_1 =
  "OK, we need to pick you a body. We only bought the brain — we can save more people that way.";
const LINE_BODY_OPENER_2 =
  "Your heart and lungs went to their longevity programme. They value those rather more than the person they came with.";

export interface PlayerState {
  name: string;
  body: string;
}

export function runIntake(root: HTMLElement): Promise<PlayerState> {
  return new Promise((resolve) => {
    root.innerHTML = "";
    root.className = "act2-intake";

    // Background — fades up from black
    const scene = el("div", "intake-scene");
    if (SCENE_BG) scene.style.backgroundImage = `url("${SCENE_BG}")`;

    // [SIMULATION] label — hovers above the input box, fades in after player has seen the scene
    const simTag = el("div", "sim-tag");
    simTag.textContent = "[SIMULATION]";

    // Scrolling chat log
    const log = el("div", "intake-log");

    // Input row
    const inputRow = el("div", "intake-input-row");
    inputRow.style.display = "none";
    const textInput = document.createElement("input");
    textInput.type = "text";
    textInput.className = "intake-input";
    textInput.autocomplete = "new-password"; // suppresses credential/autofill UI in all major browsers
    const sendBtn = document.createElement("button");
    sendBtn.className = "intake-send";
    sendBtn.textContent = "→";
    inputRow.append(textInput, sendBtn);

    // Continue button — revealed after first body-conversation reply
    const continueBtn = document.createElement("button");
    continueBtn.className = "intake-continue";
    continueBtn.textContent = "Continue →";
    continueBtn.style.display = "none";

    root.append(scene, log, simTag, inputRow, continueBtn);

    // ─── Helpers ────────────────────────────────────────────────
    function appendMind(text: string): HTMLElement {
      const b = el("div", "bubble bubble-mind");
      b.textContent = text;
      log.append(b);
      log.scrollTop = log.scrollHeight;
      return b;
    }

    function appendPlayer(text: string) {
      const b = el("div", "bubble bubble-player");
      b.textContent = text;
      log.append(b);
      log.scrollTop = log.scrollHeight;
    }

    function appendSystem(text: string) {
      const b = el("div", "bubble bubble-system");
      b.textContent = text;
      log.append(b);
      log.scrollTop = log.scrollHeight;
    }

    function awaitInput(placeholder: string): Promise<string> {
      return new Promise((r) => {
        textInput.value = "";
        textInput.placeholder = placeholder;
        inputRow.style.display = "";
        textInput.focus();

        function submit() {
          const val = textInput.value.trim();
          if (!val) return;
          inputRow.style.display = "none";
          r(val);
        }
        sendBtn.onclick = submit;
        textInput.onkeydown = (e) => {
          if (e.key === "Enter") submit();
        };
      });
    }

    // ─── Main flow ──────────────────────────────────────────────
    const history: HistoryTurn[] = [];
    const playerState: PlayerState = { name: "", body: "" };

    async function run() {
      // Scene fades in from black
      await wait(80); // allow one paint frame
      scene.classList.add("scene-in");
      await wait(1800); // let them see the place

      // [SIMULATION] tag fades in rapidly
      simTag.classList.add("sim-tag-in");
      await wait(1000);

      // WAKE
      appendMind(LINE_WAKE);
      await wait(2800);

      // NAME — question stands with no preamble
      appendMind(LINE_NAME);
      const name = await awaitInput("your name");
      playerState.name = name;
      appendPlayer(name);

      // BODY OPENER
      await wait(600);
      appendMind(LINE_BODY_OPENER_1);
      await wait(1800);
      appendMind(LINE_BODY_OPENER_2);
      const bodyOpener = `${LINE_BODY_OPENER_1} ${LINE_BODY_OPENER_2}`;
      history.push({ role: "assistant", content: bodyOpener });
      await wait(1600);

      // BODY CONVERSATION — free-form LLM
      const bodyHint = el("div", "body-hint");
      bodyHint.textContent = "describe the body you want";
      log.append(bodyHint);

      textInput.value = "";
      textInput.placeholder = "describe your body…";
      inputRow.style.display = "";
      textInput.focus();

      const MAX_TURNS = 20;
      let bodyTurns = 0;
      let sending = false;
      let firstReply = true;

      function lockInput() {
        inputRow.style.display = "none";
        appendMind("That's plenty to work with — continue whenever you're ready.");
        continueBtn.style.display = "";
      }

      async function sendBody(utterance: string) {
        if (sending) return;
        sending = true;
        sendBtn.disabled = true;

        appendPlayer(utterance);
        history.push({ role: "user", content: utterance });
        bodyTurns++;

        const thinking = appendMind("…");
        thinking.classList.add("bubble-thinking");

        const gameState = bodyTurns >= 15 ? { wrap_up: true } : {};
        const result = await callIntake("intake_submit", utterance, history, gameState);

        log.removeChild(thinking);
        if (result.text) {
          appendMind(result.text);
          history.push({ role: "assistant", content: result.text });
        }

        if (firstReply) {
          continueBtn.style.display = "";
          firstReply = false;
        }

        if (bodyTurns >= MAX_TURNS) {
          lockInput();
          return;
        }

        sending = false;
        sendBtn.disabled = false;
        textInput.focus();
      }

      sendBtn.onclick = () => {
        const val = textInput.value.trim();
        if (!val || sending || bodyTurns >= MAX_TURNS) return;
        textInput.value = "";
        void sendBody(val);
      };
      textInput.onkeydown = (e) => {
        if (e.key === "Enter") sendBtn.click();
      };

      await new Promise<void>((r) => {
        continueBtn.onclick = () => {
          continueBtn.style.display = "none";
          inputRow.style.display = "none";
          r();
        };
      });

      playerState.body = history
        .filter((h) => h.role === "user")
        .map((h) => h.content)
        .join(" ");

      // WELCOME REVEAL — three beats, not one block
      await wait(800);
      appendSystem("Body order confirmed. Fabrication queued. You are almost done here.");
      await wait(1800);
      appendMind("Oh!");
      await wait(900);
      appendMind("Where are my manners!");
      await wait(2800);
      appendMind("Welcome to O:ALSO!");
      await wait(2000);

      // Fade everything out
      simTag.classList.remove("sim-tag-in");
      scene.classList.remove("scene-in");
      await wait(1600);

      resolve(playerState);
    }

    run();
  });
}

function wait(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function el(tag: string, className: string): HTMLElement {
  const e = document.createElement(tag);
  e.className = className;
  return e;
}
